import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { IMG } from "@/lib/api";
import { PageHeader } from "@/components/shared/SectionHeader";
import UsStateMap from "@/components/shared/UsStateMap";
import { Scale, FileText, MapPin, ExternalLink } from "lucide-react";

export default function LegalCenter() {
  const [states, setStates] = useState({});
  const [selected, setSelected] = useState("MT");

  useEffect(() => { api.get("/legal/states").then(r => setStates(r.data)); }, []);

  const info = states[selected];

  return (
    <div>
      <PageHeader
        eyebrow="Legal Resource Center"
        title="Know the law before you bring one home."
        description="An interactive, state-by-state guide to wolfdog legality, permits and ordinances across the United States."
        image={IMG.forest}
      />

      <section className="container-x py-14 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          <div className="eyebrow">Interactive map</div>
          <h2 className="font-display text-3xl md:text-4xl mt-3 mb-6 leading-tight">Click any state to view its rules.</h2>
          <UsStateMap states={states} selected={selected} onSelect={setSelected} />
          <p className="mt-6 text-xs text-muted-foreground max-w-lg">
            This map is a general reference. Wolfdog legality is layered — state, county, city and even HOA rules can each apply.
            Always verify with your local authorities before acquiring, importing or breeding a wolfdog.
          </p>
        </div>

        <aside className="lg:col-span-4">
          <div className="sticky top-28 rounded-lg border border-border bg-card p-6">
            {info ? (
              <div>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="eyebrow">Selected state</div>
                    <div className="font-display text-4xl mt-1 leading-none">{info.name}</div>
                  </div>
                  <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-widest rounded-full text-white whitespace-nowrap"
                    style={{ backgroundColor: `hsl(var(--legal-${{ legal: "ok", regulated: "reg", restricted: "res", banned: "ban" }[info.status]}))` }}>
                    {info.label || info.status}
                  </div>
                </div>

                <div className="mt-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <Scale className="h-4 w-4 mt-0.5 text-muted-foreground" />
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Summary</div>
                      <div className="text-sm mt-1">{info.summary}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FileText className="h-4 w-4 mt-0.5 text-muted-foreground" />
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Permit</div>
                      <div className="text-sm mt-1">{info.permit || "No state permit required"}</div>
                    </div>
                  </div>
                  {info.notes && (
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Details</div>
                        <div className="text-sm mt-1">{info.notes}</div>
                      </div>
                    </div>
                  )}
                </div>

                <a href="#" className="btn-outline w-full mt-6 justify-center" data-testid="legal-view-source">
                  View official legislation <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            ) : (
              <div className="text-muted-foreground">Select a state to view details.</div>
            )}
          </div>
        </aside>
      </section>

      {/* Disclaimer / glossary */}
      <section className="container-x pb-24 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="rounded-lg border border-border bg-card p-6">
          <div className="eyebrow">Important</div>
          <h3 className="font-display text-2xl mt-2 leading-tight">Always verify at the local level.</h3>
          <p className="mt-3 text-sm text-muted-foreground">
            In many states, counties, cities and towns, it is illegal to own a wolfdog. Even where state law permits ownership, there are likely restrictions on wolf-content percentage and additional permitting requirements. Anyone interested in owning a wolfdog must research thoroughly and comply with all laws and permits at both state and local levels before acquiring an animal.
          </p>
        </div>
        <div className="rounded-lg border border-border bg-card p-6">
          <div className="eyebrow">Glossary</div>
          <div className="mt-2 font-display text-2xl leading-tight">Wolfdog (Hybrid Animal)</div>
          <p className="mt-3 text-sm text-muted-foreground">
            A cross between a gray wolf (<em>Canis lupus</em>) and a domestic dog, with varying levels of wolf ancestry that can affect behavior and legal classification. In some jurisdictions, ownership is permitted only under strict conditions such as registration, sterilization and containment. In others, ownership is fully prohibited — and even animals that resemble wolves may be classified as such under state law.
          </p>
        </div>
      </section>
    </div>
  );
}
